// Unified pick: given a world-space ray, return the voxel the user is
// pointing at OR the empty cell on a work-volume surface they'd place a
// new voxel into. Closer hit wins. Returns null if the ray misses both.
//
// All world↔grid transforms happen here so the call sites (draw, erase,
// cursor visuals) work in cell coordinates.

import * as THREE from "three";
import { CELL_SIZE, VoxelGrid } from "../model/voxel-grid";
import {
  type CellBox,
  type FaceNormal,
  type VoxelHit,
  faceNormal,
  raycastVoxelGrid,
} from "../model/voxel-pick";
export type { CellBox };
import type { WorkVolumeSurface } from "../scene/work-volume";

export interface VoxelTarget {
  kind: "voxel";
  ix: number;
  iy: number;
  iz: number;
  // Face of (ix,iy,iz) the ray entered through. The empty cell that a
  // "draw" would fill is (ix,iy,iz) + faceNormal(face).
  face: FaceNormal;
  // Grid-local distance from the ray origin to the hit point.
  distance: number;
}

export interface SurfaceTarget {
  kind: "surface";
  // The empty cell that a "draw" would fill (just inside the surface).
  ix: number;
  iy: number;
  iz: number;
  // Outward normal of the surface (so faceNormal-style triple). For the
  // floor this is (0,-1,0); the cell sits one step in the +Y direction.
  normal: [number, number, number];
  distance: number;
}

export type PickTarget = VoxelTarget | SurfaceTarget | null;

// Half-open cell bounds for a given volume size. A cell (ix,iy,iz) is
// inside iff ix∈[minIx, maxIx), iy∈[minIy, maxIy), iz∈[minIz, maxIz).
export interface CellBounds {
  minIx: number;
  maxIx: number;
  minIy: number;
  maxIy: number;
  minIz: number;
  maxIz: number;
}

// Asymmetric work-volume extents (gridGroup-local metres) → half-open cell
// bounds. Edges are rounded to the nearest cell (callers snap the metre
// edges to whole cells, so each wall lands on its own cell line — which is
// what lets the volume have an odd cell count on any axis). Floor at y=0.
export function cellBoundsForVolume(
  minX: number,
  maxX: number,
  minZ: number,
  maxZ: number,
  height: number,
): CellBounds {
  return {
    minIx: Math.round(minX / CELL_SIZE),
    maxIx: Math.round(maxX / CELL_SIZE),
    minIy: 0,
    maxIy: Math.round(height / CELL_SIZE),
    minIz: Math.round(minZ / CELL_SIZE),
    maxIz: Math.round(maxZ / CELL_SIZE),
  };
}

export function cellInBounds(
  ix: number,
  iy: number,
  iz: number,
  b: CellBounds,
): boolean {
  return (
    ix >= b.minIx &&
    ix < b.maxIx &&
    iy >= b.minIy &&
    iy < b.maxIy &&
    iz >= b.minIz &&
    iz < b.maxIz
  );
}

const _originLocal = new THREE.Vector3();
const _dirLocal = new THREE.Vector3();
const _matInv = new THREE.Matrix4();
const _surfaceRaycaster = new THREE.Raycaster();
const _surfaceMeshList: THREE.Object3D[] = [];

// Project a world-space ray (origin + dir) into the grid's local frame.
// gridGroup's matrixWorld must be up to date — caller's responsibility.
function rayToGridLocal(
  originWorld: THREE.Vector3,
  dirWorld: THREE.Vector3,
  gridGroup: THREE.Object3D,
): void {
  _matInv.copy(gridGroup.matrixWorld).invert();
  _originLocal.copy(originWorld).applyMatrix4(_matInv);
  // transformDirection ignores translation and normalizes — exactly what
  // we want to send the direction through gridGroup's inverse rotation
  // (and any non-uniform scale would be a problem, but gridGroup uses a
  // uniform scale, so this is fine).
  _dirLocal.copy(dirWorld).transformDirection(_matInv);
}

// Compute the grid-local hit distance for a voxel hit by intersecting
// the ray with the plane of the entered face.
function voxelHitDistance(hit: VoxelHit): number {
  const [nx, ny, nz] = faceNormal(hit.face);
  let planeCoord: number;
  let originAxis: number;
  let dirAxis: number;
  if (nx !== 0) {
    // Entering face at x = ix*CELL or (ix+1)*CELL depending on outward
    // normal sign (-X face is at low corner; +X face at high corner).
    planeCoord = nx > 0 ? (hit.ix + 1) * CELL_SIZE : hit.ix * CELL_SIZE;
    originAxis = _originLocal.x;
    dirAxis = _dirLocal.x;
  } else if (ny !== 0) {
    planeCoord = ny > 0 ? (hit.iy + 1) * CELL_SIZE : hit.iy * CELL_SIZE;
    originAxis = _originLocal.y;
    dirAxis = _dirLocal.y;
  } else {
    planeCoord = nz > 0 ? (hit.iz + 1) * CELL_SIZE : hit.iz * CELL_SIZE;
    originAxis = _originLocal.z;
    dirAxis = _dirLocal.z;
  }
  if (dirAxis === 0) return Number.POSITIVE_INFINITY;
  return (planeCoord - originAxis) / dirAxis;
}

const _surfacePosLocal = new THREE.Vector3();
const _surfaceWorldHit = new THREE.Vector3();

// Convert a surface-hit world point to the cell adjacent on the inward
// side of the surface. Falls outside the bounded cell range when the
// click was near a corner / outside the work volume.
function surfaceHitToCell(
  worldHit: THREE.Vector3,
  surface: WorkVolumeSurface,
  gridGroup: THREE.Object3D,
): { ix: number; iy: number; iz: number; normal: [number, number, number] } {
  _surfacePosLocal.copy(worldHit);
  gridGroup.worldToLocal(_surfacePosLocal);
  const inwardSign = -surface.outwardSign;
  const halfCell = CELL_SIZE / 2;
  // Nudge slightly inward along the normal so floor() lands on the cell
  // on the inside of the surface, not the cell behind it.
  if (surface.axis === 0) _surfacePosLocal.x += inwardSign * halfCell;
  else if (surface.axis === 1) _surfacePosLocal.y += inwardSign * halfCell;
  else _surfacePosLocal.z += inwardSign * halfCell;
  return {
    ix: Math.floor(_surfacePosLocal.x / CELL_SIZE),
    iy: Math.floor(_surfacePosLocal.y / CELL_SIZE),
    iz: Math.floor(_surfacePosLocal.z / CELL_SIZE),
    normal:
      surface.axis === 0
        ? [surface.outwardSign, 0, 0]
        : surface.axis === 1
          ? [0, surface.outwardSign, 0]
          : [0, 0, surface.outwardSign],
  };
}

export interface PickInput {
  originWorld: THREE.Vector3;
  dirWorld: THREE.Vector3;
  grid: VoxelGrid;
  gridGroup: THREE.Object3D;
  // Object-local frame the voxel `grid` lives in (the active SceneObject's
  // transform group). The voxel raycast + distance use this so sketching
  // follows a moved/rotated object. Surfaces (walls/floor) stay in the
  // room frame (`gridGroup`). Defaults to `gridGroup` when omitted.
  voxelSpace?: THREE.Object3D;
  workVolumeSurfaces: WorkVolumeSurface[];
  bounds: CellBounds;
  // Cells (3D "ix,iy,iz" keys) that the voxel raycast should treat as
  // empty even when present in the grid. Drag-paint passes its in-
  // gesture `placedCells` here so the laser looks past freshly-placed
  // voxels and keeps targeting the original surface.
  voxelRaycastSkip?: ReadonlySet<string>;
  // Cells that the raycast should treat as OCCUPIED even when empty
  // in the grid. Box-erase passes its in-gesture erased cells here so
  // the laser stops at the deleted face — the cursor stays on the
  // deleted voxel's position instead of drilling through to whatever
  // sits behind. Surface raycasts also respect include: a surface hit
  // at an "included" cell is suppressed so the laser doesn't fall
  // through erased cells onto a wall behind them.
  voxelRaycastInclude?: ReadonlySet<string>;
  // Region form of `include` (an inclusive cell box treated as occupied) — used
  // by box-erase to keep the laser on the original surface as it carves, without
  // a per-cell set. Cells inside it block both voxel and surface hits.
  voxelRaycastIncludeBox?: CellBox | null;
  // Region form of `skip` (an inclusive cell box treated as EMPTY) — used by a
  // live box_draw so the ray looks past the box it's filling to the original
  // surface, instead of the box deflecting its own target cell.
  voxelRaycastSkipBox?: CellBox | null;
  // When false, skip surface (wall/floor) hits entirely. The surface
  // raycast is in ROOM space, but the voxel grid is in the active
  // object's space; once that object is moved/rotated the two frames
  // diverge, so a surface hit would draw onto the object's rotated plane.
  // Until room-aligned drawing gets its own object (multi-object), the
  // caller disables surfaces while the active object is off-identity.
  includeSurfaces?: boolean;
}

export function pickRay(input: PickInput): PickTarget {
  const {
    originWorld,
    dirWorld,
    grid,
    gridGroup,
    workVolumeSurfaces,
    bounds,
    voxelRaycastSkip,
    voxelRaycastInclude,
    voxelRaycastIncludeBox,
    voxelRaycastSkipBox,
  } = input;

  const voxelSpace = input.voxelSpace ?? gridGroup;
  // Ancestors + self only. This runs once per object per hover frame, and the
  // old force-recursion (updateMatrixWorld(true)) walked the ENTIRE voxel
  // subtree — every chunk mesh — each time. The only children whose world
  // matrices matter here are the surface meshes, refreshed individually below.
  gridGroup.updateWorldMatrix(true, false);
  if (voxelSpace !== gridGroup) voxelSpace.updateWorldMatrix(true, false);
  rayToGridLocal(originWorld, dirWorld, voxelSpace);

  // Voxel hit (in the active object's local space).
  const voxelHit = raycastVoxelGrid(
    grid,
    _originLocal,
    _dirLocal,
    undefined,
    voxelRaycastSkip,
    voxelRaycastInclude,
    voxelRaycastIncludeBox,
    voxelRaycastSkipBox,
  );
  const voxelDistance = voxelHit
    ? voxelHitDistance(voxelHit)
    : Number.POSITIVE_INFINITY;

  // Surface hit (in world space — THREE's raycaster handles parent
  // transforms). Skip walls hidden by the camera-occlusion pass so the
  // laser passes through invisible near walls instead of stopping at
  // them. Also skipped wholesale when the active object's frame has
  // diverged from the room (see includeSurfaces).
  _surfaceMeshList.length = 0;
  if (input.includeSurfaces !== false) {
    for (const s of workVolumeSurfaces) {
      if (s.mesh.visible) {
        // Self-from-parent only — gridGroup's matrixWorld is current (above).
        s.mesh.updateWorldMatrix(false, false);
        _surfaceMeshList.push(s.mesh);
      }
    }
  }
  _surfaceRaycaster.set(originWorld, dirWorld);
  const surfHits = _surfaceRaycaster.intersectObjects(_surfaceMeshList, false);

  // Find the closest valid surface hit (rejects clicks outside the cube).
  let surfaceTarget: SurfaceTarget | null = null;
  for (const h of surfHits) {
    let surface: WorkVolumeSurface | undefined;
    for (const s of workVolumeSurfaces) {
      if (s.mesh === h.object) {
        surface = s;
        break;
      }
    }
    if (!surface) continue;
    _surfaceWorldHit.copy(h.point);
    const c = surfaceHitToCell(_surfaceWorldHit, surface, gridGroup);
    if (!cellInBounds(c.ix, c.iy, c.iz, bounds)) continue;
    // Skip if a voxel already occupies that cell — a draw there would
    // be a no-op and the user's pick is more likely a voxel face.
    // voxelRaycastSkip is honored here too so placed-this-gesture
    // voxels are treated as transparent on the surface raycast as
    // well, otherwise drag-paint along a wall stops as soon as the
    // first placed voxel lands "on" the wall cell. voxelRaycastInclude
    // is the inverse: cells the gesture has erased should still block
    // surface hits (we want the cursor to stay on the deleted face,
    // not fall through to the canvas wall behind).
    // Only build the key string when a per-cell set is actually in play —
    // the common hover pick passes neither, and this loop runs every frame.
    const cellKey =
      voxelRaycastInclude || voxelRaycastSkip ? `${c.ix},${c.iy},${c.iz}` : null;
    if (voxelRaycastInclude && voxelRaycastInclude.has(cellKey!)) continue;
    if (
      voxelRaycastIncludeBox &&
      c.ix >= voxelRaycastIncludeBox.x0 && c.ix <= voxelRaycastIncludeBox.x1 &&
      c.iy >= voxelRaycastIncludeBox.y0 && c.iy <= voxelRaycastIncludeBox.y1 &&
      c.iz >= voxelRaycastIncludeBox.z0 && c.iz <= voxelRaycastIncludeBox.z1
    ) {
      continue;
    }
    if (
      grid.has(c.ix, c.iy, c.iz) &&
      !(voxelRaycastSkip && voxelRaycastSkip.has(cellKey!))
    ) {
      continue;
    }
    surfaceTarget = {
      kind: "surface",
      ix: c.ix,
      iy: c.iy,
      iz: c.iz,
      normal: c.normal,
      distance: h.distance,
    };
    break;
  }

  // Pick closer hit. Voxel distance is in grid-local units, surface
  // distance in world units; convert voxel to world by multiplying by
  // gridGroup's cumulative world-space scale. Has to be the matrixWorld
  // scale, not gridGroup.scale alone — the gridGroup is parented under
  // `world`, which the user can pinch-scale via two-grip navigation. If
  // we used only the local scale, voxel distances would be over- or
  // under-estimated relative to surface distances whenever world.scale
  // ≠ 1, and the pickRay comparison below could return the wrong target
  // (e.g. a far floor surface beating a close voxel face).
  const gridWorldScale = voxelSpace.matrixWorld.getMaxScaleOnAxis() || 1;
  const voxelWorldDist = voxelDistance * gridWorldScale;

  if (voxelHit && (!surfaceTarget || voxelWorldDist <= surfaceTarget.distance)) {
    return {
      kind: "voxel",
      ix: voxelHit.ix,
      iy: voxelHit.iy,
      iz: voxelHit.iz,
      face: voxelHit.face,
      distance: voxelWorldDist,
    };
  }
  return surfaceTarget;
}
